import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Space, Form, Input } from 'antd';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeComponents } from '../../../themes';

type DeleteCompanyModalProps = {
    onClose: () => void;
    onConfirm: () => void;
    open: boolean;
};

const CONFIRMATION_TEXT = 'DELETE';

const DeleteCompanyModal = ({ onClose, onConfirm, open }: DeleteCompanyModalProps) => {
    const { t } = useTranslation(['companyDetails']);
    const [form] = Form.useForm<{ confirm: string }>();
    const confirmValue = Form.useWatch('confirm', form);
    const { Modal } = useThemeComponents();

    return (
        <Modal
            cancelText={t('companyDetails:deleteModal.cancelText')}
            okButtonProps={{
                form: 'deleteCompanyForm',
                htmlType: 'submit',
                disabled: confirmValue !== CONFIRMATION_TEXT,
                danger: true,
            }}
            okText={t('companyDetails:deleteModal.okText')}
            onCancel={onClose}
            onOk={onConfirm}
            open={open}
            title={
                <Space align="center" size="middle">
                    <ExclamationCircleOutlined style={{ color: '#FF4D4F' }} />
                    <span>{t('companyDetails:deleteModal.title')}</span>
                </Space>
            }
        >
            <p>{t('companyDetails:deleteModal.content')}</p>

            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                    label={t('companyDetails:deleteModal.confirmationLabel', { CONFIRMATION_TEXT })}
                    name="confirm"
                    rules={[
                        {
                            required: true,
                            message: t('companyDetails:deleteModal.confirmationRequired'),
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export const useDeleteCompanyModal = () => {
    const [open, setOpen] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }),
        [setOpen]
    );

    return {
        ...actions,
        render: (onConfirm: () => void) => (
            <DeleteCompanyModal onClose={actions.close} onConfirm={onConfirm} open={open} />
        ),
    };
};

export default DeleteCompanyModal;
